/**
 * JAVASCRIPT DEVELOPER DOCUMENTATION
 *
 * Symmetry is a powerful and customizable theme designed for large-scale e-commerce stores. Built
 * using Web Components, it offers a highly modular architecture that makes customization and
 * maintenance easier than ever. In addition, Symmetry is optimized for speed, ensuring that your
 * store runs as fast as possible to provide your customers with a seamless shopping experience.
 *
 * If you would like to add your own JS to Symmetry, we recommend using this file and referencing
 * it using Theme Settings > Advanced > Custom HTML.
 *
 * As a brief overview, Symmetry:
 *  - Broadcasts many JS events.
 *  - Is built using Web Components.
 *  - Leverages 'code splitting' for some features.
 *  - Is completely custom built (no JS libraries)
 *  - Has a number of JS utilities.
 *
 *
 *
 * =================================================================================================
 * Custom JavaScript Events
 * =================================================================================================
 *
 * Symmetry broadcasts many custom events for ease of extensibility, detailed in this section.
 *
 * When in the Theme Editor, the details of each custom event will be logged out in the Dev Tools
 * console everytime it is triggered.
 *
 * Events are named in the following convention: ["on/dispatch"]:[subject]:[action] (where
 * 'dispatch' will trigger an event to occur, and 'on' indicates an event has occurred).
 *
 * All 'Return data' detailed in this section is returned within the 'event.detail' object.
 *
 * The available events are:
 *  1.  on:variant:change
 *  2.  on:cart:add
 *  3.  on:cart:error
*   4.  on:cart:after-merge
 *  5.  on:cart-drawer:before-open
 *  6.  on:cart-drawer:after-open
 *  7.  on:cart-drawer:after-close
 *  8.  on:quickbuy:before-open
 *  9.  on:quickbuy:after-open
 *  10. on:quickbuy:after-close
 *  11. dispatch:cart-drawer:open
 *  12. dispatch:cart-drawer:refresh
 *  13. dispatch:cart-drawer:close
 *  14. on:debounced-resize
 *  15. on:breakpoint-change
 *
 * -------------------------------------------------------------------------------------------------
 * 1) on:variant:change
 * -------------------------------------------------------------------------------------------------
 * Fires whenever a variant is selected (e.g. Product page, Quickbuy, Featured Product etc).
 *
 * How to listen:
 * document.addEventListener('on:variant:change', (event) => {
 *  // your code here
 * });
 *
 * Returned data:
 *  - form: the product form content
 *  - variant: the selected variant object
 *  - allVariants: an array of all variants
 *  - selectedOptions: an array of currently selected options (e.g. ['Blue', 'Large'])
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 2) on:cart:add
 * -------------------------------------------------------------------------------------------------
 * Fires when a variant has been added to the cart, where it didn't exist in the cart before. This
 * event does not fire when the added variant was already in the cart.
 *
 * How to listen:
 * document.addEventListener('on:cart:add', (event) => {
 *   // your code here
 * });
 *
 * Returned data:
 *   - variantId: id of the variant that was just added to the cart
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 3) on:cart:error
 * -------------------------------------------------------------------------------------------------
 * Fires when an action related to the cart has failed, for example adding too much quantity of an
 * item to the cart.
 *
 * How to listen:
 * document.addEventListener('on:cart:error', (event) => {
 *   // your code here
 * });
 *
 * Returned data:
 *   - error: the error message
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 4) on:cart:after-merge
 * -------------------------------------------------------------------------------------------------
 * Fires after a list of cart items has finished being dynamically updated after a cart change.
 *
 * How to listen:
 * document.addEventListener('on:cart:after-merge', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 5) on:cart-drawer:before-open
 * -------------------------------------------------------------------------------------------------
 * Fires before the cart drawer opens.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:before-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 6) on:cart-drawer:after-open
 * -------------------------------------------------------------------------------------------------
 * Fires after the cart drawer has finished opening.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:after-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 7) on:cart-drawer:after-close
 * -------------------------------------------------------------------------------------------------
 * Fires after the cart drawer has finished closing.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:after-close', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 8) on:quickbuy:before-open
 * -------------------------------------------------------------------------------------------------
 * Fires before the quick buy drawer opens.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:before-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 9) on:quickbuy:after-open
 * -------------------------------------------------------------------------------------------------
 * Fires after the quick buy drawer has finished opening.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:after-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 10) on:quickbuy:after-close
 * -------------------------------------------------------------------------------------------------
 * Fires after the quick buy drawer has finished closing.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:after-close', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 11) dispatch:cart-drawer:open
 * -------------------------------------------------------------------------------------------------
 * Opens the cart drawer (if enabled in the Theme Settings).
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open'));
 *
 * You can optionally pass in a 'detail' object with a property of 'opener', which specifies the DOM
 * element that should be focussed on when the drawer is closed.
 *
 * Example:
 * document.getElementById('header-search').addEventListener('keydown', (evt) => {
 *   if (evt.keyCode === 67) {
 *     evt.preventDefault();
 *     document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open', {
 *       detail: {
 *         opener: evt.target
 *       }
 *     }));
 *   }
 * });
 *
 * In this example, we attach a keydown listener to the search input in the header. If the user
 * presses the 'c' key, it prevents the default behavior (which would be to type the letter 'c' in
 * the input) and dispatches the 'dispatch:cart-drawer:open' event with a 'detail' object that
 * specifies the search input as the opener. When the cart drawer is closed, focus is returned to
 * the search input.
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 12) dispatch:cart-drawer:refresh
 * -------------------------------------------------------------------------------------------------
 * Refreshes the contents of the cart drawer.
 *
 * This event is useful when you are adding variants to the cart and would like to instruct the
 * theme to re-render the cart drawer.
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:refresh', {
 *   bubbles: true
 * }));
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 13) dispatch:cart-drawer:close
 * -------------------------------------------------------------------------------------------------
 * Closes the cart drawer.
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:close'));
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 14) on:debounced-resize
 * -------------------------------------------------------------------------------------------------
 * Fires when the viewport finishes resizing (debounced to 300ms by default).
 *
 * How to listen:
 * window.addEventListener('on:debounced-resize', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 15) on:breakpoint-change
 * -------------------------------------------------------------------------------------------------
 * Fires when the breakpoint of the viewport changes.
 *
 * Example:
 * window.addEventListener('on:breakpoint-change', (event) => {
 *  if (theme.mediaMatches.md) {
 *   console.log('we are not on mobile');
 *  }
 * });
 *
 *
 *
 * =================================================================================================
 * Web Components
 * =================================================================================================
 *
 * Symmetry utilizes Web Components to the fullest.
 *
 * Web Components are a set of standardized APIs that allow developers to create custom, reusable
 * HTML elements that can be used across different web pages and applications.
 * Web Components consist of three main technologies: Custom Elements, Shadow DOM and HTML
 * Templates.
 *
 * See Mozilla for more: https://developer.mozilla.org/en-US/docs/Web/Web_Components
 *
 *
 *
 =================================================================================================
 * Third-Party JavaScript Dependencies
 * =================================================================================================
 *
 * Symmetry has no third-party JavaScript dependencies.
 *
 *
 * =================================================================================================
 *
 * Have fun! - The Clean Canvas Development Team.
 */

let customPDP = {
  initialized: false,

  init: function () {
    let _this = this;

    _this.checkVariantAvailability();
    _this.updateMaxQty();

    if (document.querySelector(".js-option")) {
      document.querySelectorAll(".js-option").forEach(function (el) {
        el.addEventListener("click", (e) => {
          setTimeout(() => {
            _this.checkVariantAvailability();
            _this.updateMaxQty();
          }, .5);
        });
      });
    }

    if (document.querySelector('input[name="quantity"]')) {
      document.querySelector('input[name="quantity"]').addEventListener('change', (e) => {
        _this.updateMaxQty();
        let maxQty = parseInt(e.target.getAttribute('max'));
        if (parseInt(e.target.value) > maxQty) e.target.value = maxQty;
      });
    }

  },

  checkVariantAvailability: function () {
    let cont = document.querySelector('.product-form variant-picker');
    if (!cont) return;
    let options = cont.querySelectorAll('.option-selector');
    let atcButton = document.querySelector('.product-form button[type="submit"]');
    let qtyTarget = document.querySelector('quantity-wrapper input[name="quantity"]');
    let variants = window.variantsJSON;
  
    let selectedOptions = [];
  
    options.forEach(option => {
      let selectedOptionEl = option.querySelector('input:checked');
      if (selectedOptionEl != null) {
        selectedOptions.push(selectedOptionEl.value);
      }
    });
  
    let currentVariant = null;
    for (const [key, variant] of Object.entries(variants)) {
      let variantOptions = key.split(' / ');
      if (variantOptions.length === selectedOptions.length &&
          variantOptions.every((val, index) => val.trim() === selectedOptions[index].trim())) {
        currentVariant = variant;
        break;
      }
    }
  
    if (!currentVariant || typeof currentVariant.inventory_management == "undefined" || !currentVariant.inventory_management || currentVariant.inventory_management.length < 1) {
      return;
    }
  
    let maxQty = null;
    if (currentVariant && typeof currentVariant.inventory_quantity != "undefined" && qtyTarget) {
      maxQty = currentVariant.inventory_quantity;
      if (maxQty < 0) maxQty = 0;
      qtyTarget.setAttribute('max', maxQty);
      if (parseInt(qtyTarget.value) > maxQty) qtyTarget.value = maxQty;
    }
  
    if (!currentVariant && atcButton && variants.hasOwnProperty('Default Title')) {
      if (variants['Default Title'].inventory_quantity > 0) {
        let atcText = atcButton.dataset.addToCartText;
        atcButton.removeAttribute('disabled');
        atcButton.textContent = atcText;
      } else {
        atcButton.setAttribute('disabled', 'disabled');
        atcButton.textContent = 'Unavailable';
      }
      return;
    }
  
    let availableOptions1 = [];
    let availableOptions2 = [];
  
    let opt2El = document.querySelector('.option-selector[data-option-index="1"]');
    if (opt2El) {
      opt2El.querySelectorAll('input').forEach(el => {
        el.classList.add('is-unavailable');
      });
    }
  
    for (const [key, value] of Object.entries(variants)) {
      let variantOptions = key.split(' / ');
      let option1 = variantOptions[0].trim();
      let option2 = variantOptions[1] ? variantOptions.slice(1).join(' / ').trim() : null;
  
      let option1El = options[0].querySelector('input[value="' + option1 + '"]');
      let option2El = option2 ? options[1].querySelector('input[value="' + option2 + '"]') : null;
  
      if (value.inventory_quantity > 0) {
        if (!availableOptions1.includes(option1)) {
          availableOptions1.push(option1);
        }
        if (option2 && !availableOptions2.includes(option2)) {
          availableOptions2.push(option2);
        }
  
        if (option1El) {
          option1El.classList.remove('is-unavailable');
        }
        if (option2El && option1 == selectedOptions[0]) {
          option2El.classList.remove('is-unavailable');
        }
      } else {
        if (option1El && !availableOptions1.includes(option1)) {
          option1El.classList.add('is-unavailable');
        }
        if (option2El && option1 == selectedOptions[0] && !availableOptions2.includes(option2)) {
          option2El.classList.add('is-unavailable');
        }
      }
  
      if (atcButton && currentVariant) {
        let atcText = atcButton.dataset.addToCartText;
        if (currentVariant.inventory_quantity < 1) {
          atcButton.setAttribute('disabled', 'disabled');
          atcText = 'Unavailable';
        } else {
          atcButton.removeAttribute('disabled');
        }
        atcButton.textContent = atcText;
      }
    }
  },  

  updateMaxQty: function() {
    let variants = window.variantsJSON;
    if(typeof variants == "undefined" || !variants) return;

    let qtyTarget = document.querySelector('.quantity-wrapper input[name="quantity"]');
    let options = document.querySelectorAll('.option-selector');
    let selectedOption1 = null;
    let selectedOption2 = null;

    let selectedOption1El = options[0].querySelector('input:checked');
    if (selectedOption1El != null) selectedOption1 = selectedOption1El.value;
    let currentVariant = variants[selectedOption1];

    if (options.length === 2) {
      let selectedOption2El = options[1].querySelector('input:checked');
      if (selectedOption2El != null) selectedOption2 = selectedOption2El.value;
      currentVariant = variants[selectedOption1 + ' / ' + selectedOption2];
    }

    if(typeof currentVariant != "undefined" && typeof currentVariant.inventory_quantity != "undefined" && currentVariant && qtyTarget) {
      let maxQty = parseInt(currentVariant.inventory_quantity);
      if(maxQty < 0) maxQty = 0;
      if(!(maxQty == 0 && currentVariant.available)) {
        qtyTarget.setAttribute('max', maxQty);
        if(parseInt(qtyTarget.value) > maxQty) qtyTarget.value = maxQty;
      }
    }

  }

};

customPDP.init();
